import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Box, Stack, Typography, Card, CardContent, useMediaQuery, useTheme, Button, Grid } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import Brightness3Icon from '@mui/icons-material/Brightness3';
import TimelineIcon from '@mui/icons-material/Timeline';
import logo from '../assets/images/sitaare_logo.png';
import { useDispatch, useSelector } from 'react-redux';
import { setQuestion, startNewSession, setPendingLandingQuestion } from '../redux/questionSlice';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import ChatInputArea from '../components/ChatInputArea';
import SEO, { createFAQSchema } from '../components/SEO';
import BlogCard from '../components/BlogCard';
import { getLatestBlogs } from '../services/blogService';

const homeFaqs = [
  { question: 'What is Vedic astrology?', answer: 'Vedic astrology (Jyotish) is an ancient Indian system of astrology that uses the sidereal zodiac and considers planetary positions at birth to provide insights about life, personality, and future events.' },
  { question: 'How does AI astrology work?', answer: 'Our AI analyzes your birth chart using traditional Vedic principles, including planetary positions, nakshatras, and Vimshottari dasha periods to generate personalized readings.' },
  { question: 'What can I ask about?', answer: 'You can ask about love, marriage, career, health, finances, travel, family relationships, life purpose, and timing of important life events.' },
];

const featureCards = [
  {
    icon: AutoAwesomeIcon,
    title: 'Your Astrologer. Beyond AI.',
    description: 'No approximation, no hallucination, just real data. Sitaare combines your birth chart (Kundli), today\'s sky (Gochar), and the timing period you\'re in (Dasha) along with many other layers to give you a reading that\'s truly yours.',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    link: '/how-are-we-different',
    linkText: 'Find out how we\'re different →',
  },
  {
    icon: GpsFixedIcon,
    title: 'Your Kundli. To the Degree.',
    description: 'A single degree can change a planet\'s house — and the area of life it governs. Sitaare computes your Kundli with astronomical precision, for your exact birth moment and location.',
    gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
  },
  {
    icon: Brightness3Icon,
    title: 'Your Sky. Tracked Every Day.',
    description: 'Planets keep moving, and where they are today matters. Sitaare tracks Gochar — live planetary transits — and maps them to your Kundli so every reading reflects the sky right now.',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  {
    icon: TimelineIcon,
    title: 'Your Dasha. Your Chapter.',
    description: 'Vimshottari dasha maps your life into planetary chapters. Sitaare knows exactly which one you\'re living through — and factors your dasha into every answer.',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
];

const LandingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { question } = useSelector(state => state.question);
  const { isLoggedIn } = useSelector(state => state.user);
  const [activeCard, setActiveCard] = useState(0);
  const [lockedHeight, setLockedHeight] = useState(null);
  const containerRef = useRef(null);
  const hasMeasured = useRef(false);
  const touchStartX = useRef(null);
  const [latestBlogs, setLatestBlogs] = useState([]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setActiveCard(prev => (prev + 1) % featureCards.length);
      else setActiveCard(prev => (prev - 1 + featureCards.length) % featureCards.length);
    }
    touchStartX.current = null;
  };

  useLayoutEffect(() => {
    if (!hasMeasured.current && containerRef.current) {
      setLockedHeight(containerRef.current.getBoundingClientRect().height);
      hasMeasured.current = true;
    }
  });

  // Redirect logged-in users straight to chat
  useEffect(() => {
    if (isLoggedIn) navigate('/chat', { replace: true });
  }, [isLoggedIn, navigate]);

  const doSubmit = () => {
    if (question?.length > 0) {
      dispatch(setPendingLandingQuestion(question));
      const ref = searchParams.get('ref');
      navigate(ref ? `/signup?ref=${ref}` : '/signup');
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    doSubmit();
  };

  const renderCard = (card, isMdUp) => {
    const IconComponent = card.icon;
    return (
      <Card
        sx={{
          flex: 1,
          width: '100%',
          height: isMdUp ? 'auto' : '100%',
          background: card.gradient,
          color: 'white',
          borderRadius: 3,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          },
        }}
      >
        <CardContent sx={{ py: isMdUp ? 3 : 2.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <IconComponent sx={{ fontSize: isMdUp ? 26 : 22, opacity: 0.9, flexShrink: 0 }} />
            <Typography variant={isMdUp ? 'subtitle1' : 'subtitle2'} sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {card.title}
            </Typography>
          </Stack>
          <Typography variant={isMdUp ? 'body2' : 'caption'} sx={{ opacity: 0.9, lineHeight: 1.6, display: 'block', mb: card.link ? 1.5 : 0 }}>
            {card.description}
          </Typography>
          {card.link && (
            <Button
              component={Link}
              to={card.link}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.9)',
                p: 0,
                justifyContent: 'flex-start',
                '&:hover': { bgcolor: 'transparent', color: '#fff', textDecoration: 'underline' },
              }}
            >
              {card.linkText}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const FeatureCardsSection = (
    <>
      {isMdUp ? (
        <Stack direction="row" spacing={1} alignItems="stretch">
          {featureCards.map((card, index) => (
            <Box key={index} sx={{ flex: 1, display: 'flex' }}>
              {renderCard(card, true)}
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <Box
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{ width: '100%', height: lockedHeight || 'auto' }}
          >
            {renderCard(featureCards[activeCard], false)}
          </Box>
          <Stack direction="row" spacing={1} justifyContent="center">
            {featureCards.map((_, index) => (
              <Box
                key={index}
                onClick={() => setActiveCard(index)}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === activeCard ? 'primary.main' : 'rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </>
  );

  // Reset session when landing page mounts
  useEffect(() => {
    dispatch(startNewSession());
  }, [dispatch]);

  // Latest blog posts for the "Latest from the blog" section — hidden entirely if none published yet.
  useEffect(() => {
    getLatestBlogs(3).then(setLatestBlogs).catch(() => setLatestBlogs([]));
  }, []);

  // Pre-fill question from ?q= parameter (e.g., from ChatGPT GPT)
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      dispatch(setQuestion(q));
    }
  }, [searchParams, dispatch]);

  return (
    <>
      <SEO
        path="/"
        description="Get personalized Vedic astrology readings powered by AI. Analyze your birth chart, discover your nakshatra, understand Vimshottari dasha periods, and receive guidance on love, career, health, and life purpose."
        jsonLd={createFAQSchema(homeFaqs)}
      />
      <Stack
        spacing={4}
        sx={{
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          alignItems: 'center',
          overflowY: 'auto',
        }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Stack spacing={isMdUp ? 4 : 3} sx={{alignItems: 'center', px: 2, width: '100%', py: isMdUp ? 2 : 1}}>
          {/* Logo + Tagline */}
          <Stack spacing={1} sx={{ alignItems: 'center', width: isMdUp ? '280px' : '200px' }}>
            <Box component="img" src={logo} alt="Sitaare Logo" sx={{ width: '100%' }} />
            <Typography
              component="h1"
              variant="body2"
              sx={{
                textAlign: 'center',
                width: '100%',
                background: 'linear-gradient(90deg, #f37575, #a16ab5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 200,
                letterSpacing: 0.5,
                m: 0,
              }}
            >
              What do the stars tell about you?
            </Typography>
          </Stack>

          {/* Question Input Section */}
          <Box sx={{ width: '100%', maxWidth: 720, py: isMdUp ? 3 : 2 }}>
            <ChatInputArea
              question={question}
              onChange={(val) => dispatch(setQuestion(val))}
              onClear={() => dispatch(setQuestion(''))}
              onSubmit={doSubmit}
              showSuggestions
              placeholder={"Ask Sitaare — it's free, no signup required"}
            />
          </Box>

          {/* Feature Cards + Latest from the blog share one container so both rows use its
              full width naturally — no separate width to calculate or keep in sync. */}
          <Box sx={{ width: '100%', maxWidth: 900, px: 2, pt: isMdUp ? 2 : 1 }}>
            {FeatureCardsSection}

            {latestBlogs.length > 0 && (
              <Box sx={{ pt: isMdUp ? 3 : 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant={isMdUp ? 'h6' : 'subtitle1'} sx={{ fontWeight: 700 }}>
                    Latest from the blog
                  </Typography>
                  <Button
                    component={Link}
                    to="/blogs"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    View all →
                  </Button>
                </Stack>
                {isMdUp ? (
                  <Stack direction="column" spacing={2}>
                    {latestBlogs.map((blog) => (
                      <BlogCard key={blog.slug} blog={blog} />
                    ))}
                  </Stack>
                ) : (
                  <Grid container spacing={2}>
                    {latestBlogs.map((blog) => (
                      <Grid item xs={12} sm={6} key={blog.slug}>
                        <BlogCard blog={blog} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default LandingPage;
